
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { BillingStatus } from '@/types/constants';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface BillingStatusBadgeProps {
  status: BillingStatus | string;
  showTooltip?: boolean;
}

const BillingStatusBadge: React.FC<BillingStatusBadgeProps> = ({ 
  status, 
  showTooltip = true 
}) => {
  // Prevent event propagation to avoid row selection issues
  const handleInteraction = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const getVariant = () => {
    switch (status) {
      case 'current':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'overdue':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusLabel = () => {
    switch (status) {
      case 'current':
        return 'Account Paid';
      case 'overdue':
        return 'Payment Due';
      case 'inactive':
        return 'Not Billing';
      default:
        return 'Status Not Set';
    }
  };
  
  const getStatusDescription = () => {
    switch (status) {
      case 'current':
        return 'This family account is up to date with all payments';
      case 'overdue':
        return 'This family has outstanding payments that need attention';
      case 'inactive':
        return 'This family is not currently in the billing cycle';
      default:
        return 'Billing status has not been set';
    }
  };

  const badge = (
    <Badge 
      onClick={handleInteraction}
      onMouseDown={handleInteraction}
      onTouchStart={handleInteraction}
      className={`font-medium ${getVariant()} select-none pointer-events-auto`} 
      variant="outline"
    >
      {getStatusLabel()}
    </Badge>
  );

  if (!showTooltip) {
    return badge;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center gap-1">
            {badge}
            <Info className="h-4 w-4 text-muted-foreground" />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{getStatusDescription()}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default BillingStatusBadge;
